import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription, from, max } from 'rxjs';
import * as AuthActions from 'src/app/store/auth/auth.actions';
import { HomeComponent } from '../home.component';
import { AppComponent } from 'src/app/app.component';
import { AppState } from 'src/app/store/app.reducer';
import { selectAskNextRatingDate, selectCurrentLang, selectExitBeta, selectNotificationMaintenance, selectPrivacyNoteList, selectReleaseNoteList, selectUser, selectUserBetaFe } from 'src/app/store/user/user.selector';
import { ActivatedRoute, Router } from '@angular/router';
import { User } from 'src/app/model/user';
import { Role } from 'src/app/model/role';
import { selectNotificationList, selectTopbarTitle } from 'src/app/store/notifications/notifications.selector';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';
import { NotificationMaintenanceDialogComponent } from '../components/notification-maintenance-dialog/notification-maintenance.dialog.component';
import { NotificationMaintenance } from 'src/app/model/notification-maintenance';
import { LOCALSTORAGE_NOTIFICATION_MAINTENANCE_ID } from 'src/app/conf/user';
import { AppMainComponent } from 'src/app/menu/app.main.component';
import { environment } from 'src/environments/environment';
import * as UserActions from 'src/app/store/user/user.actions';
import { TokenData } from 'src/app/model/api/token-data';
import { selectAuthCodeData } from 'src/app/store/auth/auth.selector';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import * as NotificationsActions from "src/app/store/notifications/notifications.actions";
import { Notification, NotificationLevel } from 'src/app/model/notification';
import { logEvent } from 'firebase/analytics';
import { Lang } from 'src/app/model/lang';
import { DEFAULT_LANG } from 'src/app/conf/languages';
import packageJson from 'package.json';
import { ReleaseNote } from 'src/app/model/release-note';
import { ReleaseNoteDialogComponent } from 'src/app/modules/shared/components/release-note-dialog/release-note.dialog.component';
import { PrivacyNoteDialogComponent } from 'src/app/modules/shared/components/privacy-note-dialog/privacy-note.dialog.component';
import { RatingDialogComponent } from 'src/app/modules/shared/components/rating-dialog/rating-dialog.component';
import { selectStatusData } from 'src/app/store/task/task.selector';
import { StatusData } from 'src/app/model/api/data-rest';
import moment from 'moment';

@Component({
	selector: 'app-user-topbar',
	templateUrl: './user.topbar.component.html',
	providers: [DialogService, AppMainComponent],
	styles: [
		`
		#notificationList {
			max-height: 700px;
			width: 270px;
			overflow: auto;
		}
		`
	]
})

export class UserTopBarComponent implements OnInit, OnDestroy {
	// Observables and subscriptions
	titleSel$: Observable<string>;
	titleSub: Subscription | undefined;
	userBetaFeSel$: Observable<number | undefined>;
	userBetaFeSelSub: Subscription | undefined;
	tokenData$: Observable<TokenData | undefined>;
	tokenDataSub: Subscription | undefined;
	exitBeta$: Observable<boolean | undefined>;
	exitBetaSub: Subscription | undefined;

	releaseNoteList?: ReleaseNote[];
	releaseNoteList$: Observable<ReleaseNote[]>;
	releaseNoteListSub?: Subscription;
	privacyNoteList?: ReleaseNote[];
	privacyNoteList$: Observable<ReleaseNote[]>;
	privacyNoteListSub?: Subscription;

	notificationMaintenance?: NotificationMaintenance[];
	notificationMaintenance$: Observable<NotificationMaintenance[]>;
	notificationMaintenanceSub?: Subscription;

	notificationListSel$: Observable<Notification[] | undefined>;
	notificationListSub: Subscription | undefined;
	notificationList: Notification[] = [];

	statusMaintanance$: Observable<StatusData | undefined>;
	statusMaintananceSub: Subscription | undefined;

	askNextRatingDate$: Observable<string | undefined>;
	askNextRatingDateSub: Subscription | undefined;

	betaFe: number;
	titleId = '';
	tokenData?: TokenData;

	currentUser?: User;
	user$: Observable<User | undefined>;
	userSub: Subscription | undefined;

	currentRole?: Role;

	currentLang$: Observable<Lang> = new Observable();
	currentLang: Lang = DEFAULT_LANG;

	// Design
	screenWidth: number;

	showInfoAvatar: boolean;
	dynamicTitle: string;

	ref: DynamicDialogRef | undefined;

	enableBeta: boolean = false;

	constructor(
		public homeMain: HomeComponent,
		public appMain: AppMainComponent,
		public app: AppComponent,
		private store: Store<AppState>,
		private router: Router,
		private route: ActivatedRoute,
		public dialogService: DialogService,
		private translate: TranslateService,
	) {
		this.initSelectors();
	}

	ngOnInit(): void {
		this.store.dispatch(UserActions.getNextRatingVote());
		this.subscribeAll();
		this.onResize();

		/*const viewNewFrontend = localStorage.getItem(LOCALSTORAGE_BETA_FE); //TOREMOVE in futuro
		if (!viewNewFrontend || viewNewFrontend !== 'true') {
			this.viewBetaFE();
		}*/
	}

	ngOnDestroy(): void {
		this.userSub?.unsubscribe();
		this.userBetaFeSelSub?.unsubscribe();
		this.titleSub?.unsubscribe();
		this.notificationMaintenanceSub?.unsubscribe();
		this.tokenDataSub?.unsubscribe();
		this.exitBetaSub?.unsubscribe();
		this.notificationListSub?.unsubscribe();
		this.releaseNoteListSub?.unsubscribe();
		this.privacyNoteListSub?.unsubscribe();
		this.statusMaintananceSub?.unsubscribe();
		this.askNextRatingDateSub?.unsubscribe();
	}

	initSelectors(): void {
		// Store selectors
		this.user$ = this.store.select(selectUser);
		this.userBetaFeSel$ = this.store.select(selectUserBetaFe);
		this.titleSel$ = this.store.select(selectTopbarTitle);
		this.notificationMaintenance$ = this.store.select(selectNotificationMaintenance);
		this.tokenData$ = this.store.select(selectAuthCodeData);
		this.exitBeta$ = this.store.select(selectExitBeta);
		this.notificationListSel$ = this.store.select(selectNotificationList);
		this.currentLang$ = this.store.select(selectCurrentLang);
		this.releaseNoteList$ = this.store.select(selectReleaseNoteList);
		this.privacyNoteList$ = this.store.select(selectPrivacyNoteList);
		this.statusMaintanance$ = this.store.select(selectStatusData);
		this.askNextRatingDate$ = this.store.select(selectAskNextRatingDate);
	}

	subscribeAll(): void {
		this.statusMaintananceSub = this.statusMaintanance$.subscribe(statusData => {
			if (statusData !== undefined && statusData !== null && statusData.Status !== undefined && statusData.Status > 0) {
				this.logout();
			}
		});
		this.releaseNoteListSub = this.releaseNoteList$
			.subscribe(releaseNoteList => {
				this.releaseNoteList = releaseNoteList;
				this.releaseNoteCheck();
			}
			);

		this.privacyNoteListSub = this.privacyNoteList$
			.subscribe(privacyNoteList => {
				this.privacyNoteList = privacyNoteList;
				this.privacyNoteCheck();
			}
			);
		this.userSub = this.user$?.subscribe(currentUser => {
			this.currentUser = currentUser;
			if (currentUser.JumpFe) {
				this.enableBeta = true;
			}
			const roles = currentUser?.Roles;
			if (roles && roles.length > 0) {
				this.currentRole = roles[0];
			}
			this.releaseNoteCheck();
			this.privacyNoteCheck();
		});
		this.userBetaFeSelSub = this.userBetaFeSel$?.subscribe(
			(betaFe: number) => {
				this.betaFe = betaFe;
				/*if (this.betaFe === 0) {//TODO NOT USE ANYMORE
					this.logout();
				}*/
			}
		);
		this.titleSub = this.titleSel$?.subscribe(
			(title: string) => {
				this.titleId = title;
			}
		);
		this.tokenDataSub = this.tokenData$?.subscribe(tokenData => {
			this.tokenData = tokenData;
		});
		this.exitBetaSub = this.exitBeta$?.subscribe(exitBeta => {
			if (exitBeta) {
				window.open(environment.oldFrontEnd + '/login?br=' + this.tokenData.access_token +
					'&ex=' + this.tokenData.expires_in.toString(), '_self');
			}
		});
		this.notificationMaintenanceSub = this.notificationMaintenance$
			.subscribe(notificationMaintenance => {
				this.notificationMaintenance = notificationMaintenance;
				if (notificationMaintenance && notificationMaintenance.length > 0) {
					const notificationIdStorage = localStorage.getItem(LOCALSTORAGE_NOTIFICATION_MAINTENANCE_ID);
					const ab = from(notificationMaintenance.map(notification => notification.NotificationId)).pipe(max((a, b) => a - b));
					ab.subscribe(maxId => {
						if (notificationIdStorage) {
							const notificationId = parseInt(notificationIdStorage, 10);
							if (notificationId < maxId) {
								this.viewNotificationMaintenance();
							}
						} else {
							this.viewNotificationMaintenance();
						}
					});
				}
			}
			);
		this.notificationListSub = this.notificationListSel$?.subscribe(
			(notifications: Notification[]) =>
				this.notificationsCallback(notifications)
		);
		this.currentLang$?.subscribe((lang: Lang) => {
			this.currentLang = lang;
		});
		this.askNextRatingDateSub = this.askNextRatingDate$?.subscribe(
			(askNextRatingDate: string) => {
				if (askNextRatingDate !== '') {
					let currentDate = new Date().toUTCString();
					currentDate = moment(currentDate).format('YYYY-MM-DD')
					askNextRatingDate = moment(askNextRatingDate).format('YYYY-MM-DD')
					if (currentDate === askNextRatingDate) {
						setTimeout(() => {
							this.showRatingDialog();
						}, 60000);
					}
				}
			}
		);
	}

	notificationsCallback(notifications: Notification[]): void {
		this.notificationList = notifications;
	}

	clearMessages() {
		this.homeMain.setLockMenuNotifications();
		this.store.dispatch(NotificationsActions.clearNotifications());
	}

	clickNotification(index: number): void {
		if (index < this.notificationList.length) {
			this.homeMain.setLockMenuNotifications();
			/* const route = this.notificationList[index].route;
			if (route) {
				this.router.navigate([route]);
			} */
			this.store.dispatch(NotificationsActions.removeNotification({ index }));
		}
	}

	getCurrentRole(): string {
		return (this.currentRole) ? this.translate.instant(this.currentRole.Description) : '';
	}

	userProfile(): void {
		this.router.navigate([`/${this.route.snapshot.routeConfig.path}`, 'profile']);
	}

	iconNotificationMaintenance(): boolean {
		return !!this.notificationMaintenance && this.notificationMaintenance.length > 0;
	}

	viewNotificationMaintenance(): void {
		this.ref = this.dialogService.open(NotificationMaintenanceDialogComponent, {
			header: this.translate.instant('SYSTEM_NOTIFICATION'),
			width: '70vw',
			breakpoints: { '1120px': '98vw' },
			contentStyle: { overflow: 'auto' },
			closable: false,
		});
	}

	/*viewBetaFE(): void { //TOREMOVE in futuro 
		this.ref = this.dialogService.open(BetaFeDialogComponent, {
			header: this.translate.instant('BETA_FE_TITLE_DIALOG'),
			width: '70vw',
			breakpoints: { '1120px': '98vw' },
			contentStyle: { overflow: 'auto' },
		});
		localStorage.setItem(LOCALSTORAGE_BETA_FE, 'true');
	}*/

	logout(): void {
		const analytics = this.app.initFirebase();
		const langCode = this.currentLang ? this.currentLang.code : 'no_data';
		const nation = this.currentUser ? this.currentUser.CountryName.toUpperCase() : 'no_data';
		logEvent(analytics, 'logout', { user_role: 'USER', lang: langCode, nation: nation, app_version: packageJson.version });
		this.store.dispatch(AuthActions.authLogout());
	}

	/*goToOldCameConnect(): void {//TOREMOVE in futuro
		if (!this.enableBeta) {
			const user: User = {
				...this.currentUser,
				JumpFe: 0,
			};
			this.store.dispatch(UserActions.updateCurrentUserExitBeta({ user }));
		}
	}*/

	getMenuTheme(): string {
		return this.app.menuTheme;
	}

	releaseNoteCheck(): void {
		if (this.currentUser !== undefined && this.currentUser.Email !== undefined &&
			this.currentUser.Email !== '' &&
			(this.releaseNoteList !== undefined && this.releaseNoteList.length > 0 &&
				this.currentUser.ReleaseVersion !== this.releaseNoteList[0].ReleaseVersion)) {
			this.showReleaseNote();
		}
	}

	privacyNoteCheck(): void {
		if (this.currentUser !== undefined && this.currentUser.Email !== undefined &&
			this.currentUser.Email !== '' &&
			(this.privacyNoteList !== undefined && this.privacyNoteList.length > 0 &&
				this.currentUser.PrivacyVersion !== this.privacyNoteList[0].ReleaseVersion)) {
			this.showPrivacyNote();
		}
	}

	showReleaseNote(): void {
		this.ref = this.dialogService.open(ReleaseNoteDialogComponent, {
			header: this.translate.instant('RELEASE_NOTE'),
			width: '70vw',
			breakpoints: { '1120px': '98vw' },
			contentStyle: { overflow: 'auto' },
			closable: false,
		});
		this.ref.onClose.subscribe(() => {
			this.store.dispatch(UserActions.setFeReleaseVersion({ version: this.releaseNoteList[0].ReleaseVersion }));
		});
	}

	showRatingDialog(): void {
		this.ref = this.dialogService.open(RatingDialogComponent, {
			header: this.translate.instant('RATING_DIALOG_TITLE'),
			width: '50vw',
			breakpoints: { '1120px': '98vw' },
			contentStyle: { overflow: 'auto' },
			closable: false,
		});
	}



	showPrivacyNote(): void {
		this.ref = this.dialogService.open(PrivacyNoteDialogComponent, {
			header: this.translate.instant('PRIVACY_NOTE'),
			width: '70vw',
			breakpoints: { '1120px': '98vw' },
			contentStyle: { overflow: 'auto' },
			closable: false,
		});
		this.ref.onClose.subscribe(() => {
			this.store.dispatch(UserActions.setFePrivacyVersion({ version: this.privacyNoteList[0].ReleaseVersion }));
		});
	}

	thereIsDangerNotification(): boolean {
		for (let i = 0; i < this.notificationList.length; i++) {
			const notification = this.notificationList[i];
			if (notification.level === NotificationLevel.Error) {
			return true;
			}
		}
		return false;
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
		this.setTopbar();
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	setTopbar() {
		if (StandardPageLayout.underTablet(this.screenWidth)) {
			this.showInfoAvatar = false;
		} else {
			this.showInfoAvatar = true;
		}
	}
}
